import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, Menu, X } from "lucide-react";
import { BUSINESS, NAV_LINKS } from "@/data/site";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const onHome = pathname === "/";

  // On the homepage use in-page anchors (smooth scroll); elsewhere link back to "/#id".
  const resolve = (to) => (onHome && to.startsWith("/#") ? to.slice(1) : to);
  const isAnchor = (href) => href.startsWith("#");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const NavItem = ({ label, to }) => {
    const href = resolve(to);
    const overHero = onHome && !scrolled;
    const cls =
      `text-sm transition-colors duration-200 relative after:absolute after:left-0 after:-bottom-1.5 after:h-px after:w-0 after:bg-brand hover:after:w-full after:transition-[width] after:duration-300 ${overHero ? "text-white/85 hover:text-white" : "text-concrete hover:text-stark"}`;
    if (isAnchor(href) || href.startsWith("/#"))
      return <a href={href} data-testid={`nav-${label.toLowerCase()}`} className={cls}>{label}</a>;
    return <Link to={href} data-testid={`nav-${label.toLowerCase()}`} className={cls}>{label}</Link>;
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,backdrop-filter,border-color,padding] duration-300 border-b ${
        scrolled ? "bg-void/70 backdrop-blur-xl border-hairline py-3" : "bg-transparent border-transparent py-5"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 flex items-center justify-between">
        {onHome ? (
          <a href="#top" data-testid="brand-logo" className="flex items-center">
            <img src={scrolled ? BUSINESS.logoDark : BUSINESS.logo} alt="Aims Electrical logo" className={`w-auto transition-[height] duration-300 ${scrolled ? "h-11 lg:h-12" : "h-12 lg:h-16"}`} fetchPriority="high" />
          </a>
        ) : (
          <Link to="/" data-testid="brand-logo" className="flex items-center">
            <img src={BUSINESS.logoDark} alt="Aims Electrical logo" className="h-11 lg:h-14 w-auto" fetchPriority="high" />
          </Link>
        )}

        <nav className="hidden lg:flex items-center gap-9">
          {NAV_LINKS.map((l) => <NavItem key={l.to} {...l} />)}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={BUSINESS.phoneHref}
            data-testid="header-call-cta"
            className="cta-pulse hidden sm:inline-flex items-center gap-2 bg-red-600 text-white font-semibold text-sm px-6 py-3 rounded-full shadow-lg shadow-red-600/40 ring-1 ring-white/20 hover:bg-red-700 hover:scale-[1.04] active:scale-95 transition-all duration-200"
          >
            <Phone className="w-4 h-4" strokeWidth={2.5} />
            {BUSINESS.phoneDisplay}
          </a>
          <a
            href={BUSINESS.phoneHref}
            data-testid="header-call-icon"
            aria-label={`Call ${BUSINESS.phoneDisplay}`}
            className="sm:hidden grid place-items-center w-11 h-11 rounded-lg border-2 border-red-600 text-red-600 hover:bg-red-600 hover:text-white transition-colors duration-200"
          >
            <Phone className="w-5 h-5" strokeWidth={2.5} />
          </a>
          <button data-testid="mobile-menu-toggle" className={`lg:hidden p-2 ${onHome && !scrolled ? "text-white" : "text-stark"}`} onClick={() => setOpen((o) => !o)} aria-label="Toggle menu">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-void/95 backdrop-blur-xl border-t border-hairline mt-3">
          <nav className="flex flex-col px-6 py-4">
            {NAV_LINKS.map((l) => {
              const href = resolve(l.to);
              const cls = "py-3 text-lg font-heading tracking-tight text-stark border-b border-hairline last:border-0";
              return (isAnchor(href) || href.startsWith("/#")) ? (
                <a key={l.to} href={href} data-testid={`mobile-nav-${l.label.toLowerCase()}`} onClick={() => setOpen(false)} className={cls}>{l.label}</a>
              ) : (
                <Link key={l.to} to={href} data-testid={`mobile-nav-${l.label.toLowerCase()}`} onClick={() => setOpen(false)} className={cls}>{l.label}</Link>
              );
            })}
          </nav>
        </div>
      )}
    </header>
  );
};
